import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';
import apiService from '../services/api';

type FormMode = 'create' | 'edit';

interface MobileOutageFormDialogProps {
  open: boolean;
  mode: FormMode;
  outage?: any | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'cancelled', label: 'Cancelled' },
];

const INCIDENT_TYPE_OPTIONS = [
  { value: 'unplanned', label: 'Unplanned' },
  { value: 'planned', label: 'Planned' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const DEFAULT_FORM_STATE = {
  incident_id: '',
  reference_number: '',
  status: 'active',
  incident_type: 'unplanned',
  severity: 'medium',
  impact_description: '',
  affected_services: '',
  affected_areas: '',
  impact_level: '',
  fault_location: '',
  affected_service_type: '',
  states: '' as string | string[],
  start_time: null as Date | null,
  estimated_end_time: null as Date | null,
  resolved_time: null as Date | null,
  notes: '',
  carrier_id: 'manual',
  carrier_name: 'Manual Entry',
};

const generateDefaultIncidentId = () => {
  return `MOBILE-MANUAL-${format(new Date(), 'yyyyMMddHHmmss')}`;
};

const MobileOutageFormDialog: React.FC<MobileOutageFormDialogProps> = ({
  open,
  mode,
  outage,
  onClose,
  onSuccess,
}) => {
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = mode === 'edit' && Boolean(outage);

  const initialState = useMemo(() => {
    if (!isEdit || !outage) {
      return {
        ...DEFAULT_FORM_STATE,
        incident_id: generateDefaultIncidentId(),
      };
    }

    return {
      incident_id: outage.incident_id || '',
      reference_number: outage.reference_number || '',
      status: outage.status || 'active',
      incident_type: outage.incident_type || 'unplanned',
      severity: outage.severity || 'medium',
      impact_description: outage.impact_description || '',
      affected_services: outage.affected_services || '',
      affected_areas: outage.affected_areas || '',
      impact_level: outage.impact_level || '',
      fault_location: outage.fault_location || '',
      affected_service_type: outage.affected_service_type || '',
      states: (outage.states && outage.states.length > 0) ? outage.states.join(', ') : '',
      start_time: outage.start_time ? new Date(outage.start_time) : null,
      estimated_end_time: outage.estimated_end_time ? new Date(outage.estimated_end_time) : null,
      resolved_time: outage.resolved_time ? new Date(outage.resolved_time) : null,
      notes: outage.notes || '',
      carrier_id: outage.carrier_id || 'manual',
      carrier_name: outage.carrier_name || 'Manual Entry',
    };
  }, [isEdit, outage]);

  useEffect(() => {
    if (open) {
      setFormState(initialState);
      setError(null);
    }
  }, [open, initialState]);

  const handleFieldChange = (field: string, value: any) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload: Record<string, any> = {
        incident_id: formState.incident_id?.trim() || undefined,
        reference_number: formState.reference_number?.trim() || undefined,
        status: formState.status,
        incident_type: formState.incident_type,
        severity: formState.severity,
        impact_description: formState.impact_description?.trim() || undefined,
        affected_services: formState.affected_services?.trim() || undefined,
        affected_areas: formState.affected_areas?.trim() || undefined,
        impact_level: formState.impact_level?.trim() || undefined,
        fault_location: formState.fault_location?.trim() || undefined,
        affected_service_type: formState.affected_service_type?.trim() || undefined,
        notes: formState.notes?.trim() || undefined,
        carrier_id: formState.carrier_id?.trim() || 'manual',
        carrier_name: formState.carrier_name?.trim() || 'Manual Entry',
      };

      const statesValue = typeof formState.states === 'string'
        ? formState.states.split(',').map((s) => s.trim()).filter(Boolean)
        : formState.states;
      if (statesValue && statesValue.length > 0) {
        payload.states = statesValue;
      }

      if (formState.start_time) payload.start_time = formState.start_time.toISOString();
      if (formState.estimated_end_time) payload.estimated_end_time = formState.estimated_end_time.toISOString();
      if (formState.resolved_time) payload.resolved_time = formState.resolved_time.toISOString();

      if (isEdit && outage?.id) {
        await apiService.updateMobileOutage(outage.id, payload);
      } else {
        await apiService.createMobileOutage(payload);
      }

      onClose();
      onSuccess?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save outage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEdit ? 'Edit Mobile Outage' : 'Create Mobile Outage'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle1" gutterBottom>Outage Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Incident ID"
                    fullWidth
                    value={formState.incident_id}
                    onChange={(e) => handleFieldChange('incident_id', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Reference Number"
                    fullWidth
                    value={formState.reference_number}
                    onChange={(e) => handleFieldChange('reference_number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={formState.status}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    required
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Incident Type"
                    value={formState.incident_type}
                    onChange={(e) => handleFieldChange('incident_type', e.target.value)}
                  >
                    {INCIDENT_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Severity"
                    value={formState.severity}
                    onChange={(e) => handleFieldChange('severity', e.target.value)}
                  >
                    {SEVERITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>Timing</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <DateTimePicker
                    label="Start Time"
                    value={formState.start_time}
                    onChange={(value) => handleFieldChange('start_time', value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DateTimePicker
                    label="Estimated End Time"
                    value={formState.estimated_end_time}
                    onChange={(value) => handleFieldChange('estimated_end_time', value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DateTimePicker
                    label="Resolved Time"
                    value={formState.resolved_time}
                    onChange={(value) => handleFieldChange('resolved_time', value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>Impact & Location</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Impact Description"
                    multiline
                    rows={3}
                    fullWidth
                    value={formState.impact_description}
                    onChange={(e) => handleFieldChange('impact_description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Affected Services"
                    fullWidth
                    value={formState.affected_services}
                    onChange={(e) => handleFieldChange('affected_services', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Affected Areas"
                    fullWidth
                    value={formState.affected_areas}
                    onChange={(e) => handleFieldChange('affected_areas', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Impact Level"
                    fullWidth
                    value={formState.impact_level}
                    onChange={(e) => handleFieldChange('impact_level', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fault Location"
                    fullWidth
                    value={formState.fault_location}
                    onChange={(e) => handleFieldChange('fault_location', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Affected Service Type"
                    fullWidth
                    value={formState.affected_service_type}
                    onChange={(e) => handleFieldChange('affected_service_type', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="States (comma separated)"
                    fullWidth
                    value={typeof formState.states === 'string' ? formState.states : ''}
                    onChange={(e) => handleFieldChange('states', e.target.value)}
                    placeholder="NSW, VIC, QLD"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>Carrier & Notes</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Carrier ID"
                    fullWidth
                    value={formState.carrier_id}
                    onChange={(e) => handleFieldChange('carrier_id', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Carrier Name"
                    fullWidth
                    value={formState.carrier_name}
                    onChange={(e) => handleFieldChange('carrier_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Internal Notes"
                    fullWidth
                    multiline
                    minRows={2}
                    value={formState.notes}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {isEdit ? 'Save Changes' : 'Create Outage'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default MobileOutageFormDialog;

